import { Link, useNavigate } from "react-router-dom";
import { Building2, CircleAlert, ChevronRight, ShieldCheck, TrendingUp } from "lucide-react";
import { Badge, recTone } from "../lib/ui";
import { disclosureRank, maxDisclosureOf, navItemAllowed } from "../lib/master";
import { actionOpenHref, canOpenAction } from "../lib/actionLinks";
import {
  collectBlockers,
  focusHref,
  labelsForGuide,
  stageCounts,
} from "../lib/analytics";

/** Landing page — high-level KPI tiles and chart previews only. */
export function AnalyticsCharts({ portfolio, guide }) {
  if (!portfolio?.kpis) return null;

  const { kpis, applications } = portfolio;
  const stages = stageCounts(applications).slice(0, 5);
  const labels = labelsForGuide(guide);
  const exec = disclosureRank(maxDisclosureOf(guide)) <= 2;
  const maxStage = Math.max(1, ...stages.map(([, n]) => n));

  const tiles = [
    { focus: "actions", testId: "kpi-applications", icon: Building2, tone: "blue", label: labels.projects, value: kpis.total, detail: exec ? `${kpis.authorized} sealed` : `${kpis.assessed} assessed` },
    { focus: "actions", testId: "kpi-blocked-gates", icon: CircleAlert, tone: "amber", label: labels.blockers, value: kpis.blocked_gates, detail: exec ? `${kpis.awaiting_decision ?? 0} decisions` : `${kpis.awaiting_decision ?? 0} awaiting` },
    { focus: "insights", testId: "kpi-in-wave", icon: ShieldCheck, tone: "cyan", label: labels.migration, value: kpis.in_wave ?? 0, detail: exec ? "active change" : `${kpis.production_success} G8 success` },
    { focus: "insights", testId: "kpi-value-forecast", icon: TrendingUp, tone: "green", label: labels.value, value: kpis.value_achievement_pct != null ? `${kpis.value_achievement_pct}%` : kpis.expected_savings, detail: exec ? "vs forecast" : "realized vs forecast" },
  ];

  return (
    <section className="analytics-overview analytics-charts-only" data-testid="analytics-overview">
      <div className="analytics-summary metric-grid">
        {tiles.map(({ focus, testId, icon: Icon, tone, label, value, detail }) => (
          <Link key={focus} to={focusHref(focus)} className={`metric metric-drill tone-${tone}`} data-testid={testId}>
            <span className={`metric-icon ${tone}`}><Icon size={18} /></span>
            <div>
              <small>{label}</small>
              <strong>{value}</strong>
              <span>{detail}</span>
            </div>
            <ChevronRight size={16} className="metric-chevron" />
          </Link>
        ))}
      </div>

      <div className="analytics-chart-grid">
        <Link to={focusHref("insights")} className="feature-panel analytics-panel chart-drill" data-testid="stage-report-preview">
          <div className="panel-header compact">
            <h2>{labels.pipeline}</h2>
            <ChevronRight size={18} />
          </div>
          <div className="stage-bars compact">
            {stages.map(([stage, n]) => (
              <div key={stage} className="stage-bar-row" data-testid={`stage-${stage.toLowerCase().replaceAll(" ", "-")}`}>
                <span>{stage}</span>
                <span className="stage-bar-wrap"><span className="stage-bar" style={{ width: `${(n / maxStage) * 100}%` }} /></span>
                <b>{n}</b>
              </div>
            ))}
          </div>
        </Link>

        <Link to={focusHref("insights")} className="feature-panel analytics-panel chart-drill pulse-compact" data-testid="governance-pulse-preview">
          <div className="panel-header compact">
            <h2>{labels.health}</h2>
            <ChevronRight size={18} />
          </div>
          <div className="pulse-grid pulse-grid-compact">
            <div data-testid="kpi-awaiting-decision"><ShieldCheck size={17} /><strong>{kpis.awaiting_decision ?? 0}</strong><span>{labels.awaiting}</span></div>
            <div data-testid="kpi-unknown-critical"><CircleAlert size={17} /><strong>{kpis.unknown_critical ?? 0}</strong><span>{labels.unknown}</span></div>
            <div><Building2 size={17} /><strong>{kpis.authorized}</strong><span>{labels.sealed}</span></div>
            <div><CircleAlert size={17} /><strong>{kpis.blocked_gates}</strong><span>{labels.gates}</span></div>
          </div>
        </Link>
      </div>

      {!exec && disclosureRank(maxDisclosureOf(guide)) >= 2 && Object.keys(kpis.distribution || {}).length > 0 && (
        <Link to={focusHref("insights")} className="feature-panel analytics-panel chart-drill strategy-preview" data-testid="strategy-report-preview">
          <div className="panel-header compact">
            <h2>{labels.strategy}</h2>
            <ChevronRight size={18} />
          </div>
          <div className="dist-list compact">
            {Object.entries(kpis.distribution || {}).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([name, n]) => (
              <div key={name}>
                <span className="dist-label"><span className={`dist-swatch ${recTone(name)}`} /> {name}</span>
                <b>{n}</b>
              </div>
            ))}
          </div>
        </Link>
      )}
    </section>
  );
}

export function AnalyticsBlockers({ portfolio, guide }) {
  const navigate = useNavigate();
  const blockers = collectBlockers(portfolio);
  const labels = labelsForGuide(guide);

  return (
    <section className="feature-panel analytics-panel" data-testid="blocker-report">
      <div className="panel-header">
        <div>
          <p className="kicker">DETAIL · BLOCKERS</p>
          <h2>{labels.blockersTitle}</h2>
        </div>
        <Badge tone={blockers.length ? "amber" : "green"}>{blockers.length ? `${blockers.length} open` : "Clear"}</Badge>
      </div>
      {blockers.length === 0 ? (
        <p className="muted small-note">No material blockers in the queue.</p>
      ) : (
        <table className="blocker-table">
          <thead>
            <tr><th>Project</th><th>Issue</th><th>Owner</th><th /></tr>
          </thead>
          <tbody>
            {blockers.map((row) => (
              <tr key={row.id} data-testid={`blocker-row-${(row.app || "na").toLowerCase()}`}>
                <td><strong>{row.app}</strong></td>
                <td>
                  <span className={`blocker-sev tone-${row.severity === "DECISION REQUIRED" ? "rose" : "amber"}`}>{row.severity}</span>
                  {" "}{row.title}
                </td>
                <td className="muted">{row.owner || "—"}</td>
                <td>
                  {canOpenAction(row.item || row, guide) ? (
                    <button type="button" className="link-button" onClick={() => navigate(actionOpenHref(row.item || row, guide, row.app))}>Open</button>
                  ) : (
                    <span className="muted small-note">Review</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

export function AnalyticsPipeline({ portfolio, guide }) {
  const { kpis, applications } = portfolio;
  const count = applications?.length || kpis.total || 0;
  const stages = stageCounts(applications);
  const labels = labelsForGuide(guide);

  return (
    <section className="feature-panel analytics-panel" data-testid="stage-report">
      <div className="panel-header">
        <div>
          <p className="kicker">DETAIL · PIPELINE</p>
          <h2>{labels.pipeline}</h2>
        </div>
        <Badge tone="cyan">{count} projects</Badge>
      </div>
      <div className="stage-bars">
        {stages.map(([stage, n]) => (
          <div key={stage} className="stage-bar-row" data-testid={`stage-${stage.toLowerCase().replaceAll(" ", "-")}`}>
            <span>{stage}</span>
            <span className="stage-bar-wrap"><span className="stage-bar" style={{ width: `${count ? (n / count) * 100 : 0}%` }} /></span>
            <b>{n}</b>
          </div>
        ))}
      </div>
    </section>
  );
}

export function AnalyticsHealth({ portfolio, guide }) {
  const { kpis } = portfolio;
  const labels = labelsForGuide(guide);

  return (
    <section className="feature-panel analytics-panel pulse-compact" data-testid="governance-pulse">
      <div className="panel-header">
        <div>
          <p className="kicker">DETAIL · GOVERNANCE</p>
          <h2>{labels.health}</h2>
        </div>
      </div>
      <div className="pulse-grid">
        <div data-testid="kpi-awaiting-decision"><ShieldCheck size={17} /><strong>{kpis.awaiting_decision ?? 0}</strong><span>{labels.awaiting}</span></div>
        <div data-testid="kpi-unknown-critical"><CircleAlert size={17} /><strong>{kpis.unknown_critical ?? 0}</strong><span>{labels.unknown}</span></div>
        <div><Building2 size={17} /><strong>{kpis.authorized}</strong><span>{labels.sealed}</span></div>
        <div><CircleAlert size={17} /><strong>{kpis.blocked_gates}</strong><span>{labels.gates}</span></div>
      </div>
      <p className="muted small-note">HIGH-impact UNKNOWN and open decisions block MDR authorization. This is governance health — not Jira % complete.</p>
    </section>
  );
}

export function AnalyticsStrategy({ portfolio, guide }) {
  const { kpis } = portfolio;
  const distEntries = Object.entries(kpis.distribution || {}).sort((a, b) => b[1] - a[1]);
  const maxDist = Math.max(1, ...distEntries.map(([, v]) => v));
  const labels = labelsForGuide(guide);
  const canOpenApps = guide?.can_drill !== false && navItemAllowed("/applications", guide);

  if (!distEntries.length || disclosureRank(maxDisclosureOf(guide)) < 2) {
    return <p className="muted small-note">Strategy mix is available to technical roles with project drill-down.</p>;
  }

  return (
    <section className="feature-panel analytics-panel" data-testid="strategy-report">
      <div className="panel-header">
        <div>
          <p className="kicker">DETAIL · 7R MIX</p>
          <h2>{labels.strategy}</h2>
        </div>
        {canOpenApps && <Link className="secondary-button small-btn" to="/applications">All projects</Link>}
      </div>
      <div className="dist-list compact">
        {distEntries.map(([name, n]) => (
          <div key={name}>
            <span className="dist-label"><span className={`dist-swatch ${recTone(name)}`} /> {name}</span>
            <span className="dist-bar-wrap"><span className={`dist-bar ${recTone(name)}`} style={{ width: `${(n / maxDist) * 100}%` }} /></span>
            <b>{n}</b>
          </div>
        ))}
      </div>
      <p className="muted small-note">Retain {kpis.retain_count ?? 0} · Retire {kpis.retire_count ?? 0}</p>
    </section>
  );
}

/** @deprecated use AnalyticsCharts + detail exports */
export default function AnalyticsOverview({ portfolio, guide, compact = false }) {
  if (compact) return <AnalyticsCharts portfolio={portfolio} guide={guide} />;
  return (
    <>
      <AnalyticsCharts portfolio={portfolio} guide={guide} />
      <div className="analytics-grid">
        <AnalyticsBlockers portfolio={portfolio} guide={guide} />
        <AnalyticsPipeline portfolio={portfolio} guide={guide} />
        <AnalyticsHealth portfolio={portfolio} guide={guide} />
        <AnalyticsStrategy portfolio={portfolio} guide={guide} />
      </div>
    </>
  );
}
